/**
 * Custom hook for visitor management permissions
 * Handles role-based access control for visitor features
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';

const useVisitorPermissions = () => {
  const { user, userRole, permissions } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user && permissions) {
      setLoading(false);
    }
  }, [user, permissions]);

  // Check if user has specific visitor permission
  const hasPermission = useCallback((permission) => {
    if (!permissions || !Array.isArray(permissions)) {
      return false;
    }
    return permissions.includes(permission);
  }, [permissions]);

  // Visitor management permissions
  const visitorPermissions = useMemo(() => {
    if (!permissions) {
      return {
        canManageVisitors: false,
        canViewVisitors: false,
        canCheckInVisitors: false,
        canApproveVisitors: false,
        canRegisterVisitors: false,
        canEditVisitors: false,
        canDeleteVisitors: false,
        canViewAnalytics: false,
        canExportData: false,
        canManageDocuments: false,
        canAccessAllBuildings: false
      };
    }

    return {
      // Core visitor management
      canManageVisitors: hasPermission('manage_visitors'),
      canViewVisitors: hasPermission('view_visitors') || hasPermission('manage_visitors'),
      canCheckInVisitors: hasPermission('checkin_visitors') || hasPermission('manage_visitors'),
      canApproveVisitors: hasPermission('approve_visitors') || hasPermission('manage_visitors'),
      
      // Registration and editing
      canRegisterVisitors: hasPermission('checkin_visitors') || hasPermission('manage_visitors'),
      canEditVisitors: hasPermission('manage_visitors'),
      canDeleteVisitors: hasPermission('manage_visitors'),
      
      // Analytics and reporting
      canViewAnalytics: hasPermission('view_analytics') || hasPermission('manage_visitors'),
      canExportData: hasPermission('export_data') || hasPermission('manage_visitors'),
      
      // Document management
      canManageDocuments: hasPermission('manage_documents') || hasPermission('manage_visitors'),
      
      // Building access
      canAccessAllBuildings: hasPermission('manage_buildings') || hasPermission('manage_visitors'),
    };
  }, [permissions, hasPermission]);

  // Role-based feature access
  const roleFeatures = useMemo(() => {
    const role = userRole?.toLowerCase();
    
    switch (role) {
      case 'admin':
        return {
          dashboardAccess: 'full',
          analyticsAccess: 'full',
          reportingAccess: 'full',
          settingsAccess: 'full',
          bulkOperations: true,
          advancedFilters: true,
          systemConfiguration: true
        };
      
      case 'property_manager':
        return {
          dashboardAccess: 'full',
          analyticsAccess: 'building',
          reportingAccess: 'building',
          settingsAccess: 'limited',
          bulkOperations: true,
          advancedFilters: true,
          systemConfiguration: false
        };
      
      case 'maintenance':
      case 'technician':
        return {
          dashboardAccess: 'basic',
          analyticsAccess: 'none',
          reportingAccess: 'basic',
          settingsAccess: 'none',
          bulkOperations: false,
          advancedFilters: false,
          systemConfiguration: false
        };
      
      case 'security':
        return {
          dashboardAccess: 'full',
          analyticsAccess: 'basic',
          reportingAccess: 'basic',
          settingsAccess: 'none',
          bulkOperations: false,
          advancedFilters: true,
          systemConfiguration: false
        };
      
      default:
        return {
          dashboardAccess: 'none',
          analyticsAccess: 'none',
          reportingAccess: 'none',
          settingsAccess: 'none',
          bulkOperations: false,
          advancedFilters: false,
          systemConfiguration: false
        };
    }
  }, [userRole]);

  // Check if user can perform specific action on visitor
  const canPerformAction = useCallback((action, visitor = null, context = {}) => {
    if (!visitorPermissions) return false;

    switch (action) {
      case 'view':
        return visitorPermissions.canViewVisitors;
      
      case 'register':
        return visitorPermissions.canRegisterVisitors;
      
      case 'edit':
        if (!visitorPermissions.canEditVisitors) return false;
        // Additional checks based on visitor status or user role
        if (visitor && visitor.status === 'checked_out' && userRole !== 'ADMIN') {
          return false;
        }
        return true;
      
      case 'delete':
        if (!visitorPermissions.canDeleteVisitors) return false;
        // Only allow deletion of pending or rejected visitors
        if (visitor && !['pending', 'rejected'].includes(visitor.status)) {
          return userRole === 'ADMIN';
        }
        return true;
      
      case 'approve':
        if (!visitorPermissions.canApproveVisitors) return false;
        // Can only approve pending visitors
        return !visitor || visitor.status === 'pending';
      
      case 'checkin':
        if (!visitorPermissions.canCheckInVisitors) return false;
        // Can only check in approved visitors
        return !visitor || visitor.status === 'approved';
      
      case 'checkout':
        if (!visitorPermissions.canCheckInVisitors) return false;
        // Can only check out checked-in visitors
        return !visitor || visitor.status === 'checked_in';
      
      case 'view_analytics':
        return visitorPermissions.canViewAnalytics;
      
      case 'export_data':
        return visitorPermissions.canExportData;
      
      case 'manage_documents':
        return visitorPermissions.canManageDocuments;
      
      case 'bulk_operations':
        return roleFeatures.bulkOperations;
      
      case 'advanced_filters':
        return roleFeatures.advancedFilters;
      
      default:
        return false;
    }
  }, [visitorPermissions, userRole, roleFeatures]);

  // Get filtered building access
  const getAccessibleBuildings = useCallback((allBuildings = []) => {
    if (!user || !allBuildings.length) return [];
    
    // Admin and property managers can access all buildings
    if (visitorPermissions.canAccessAllBuildings) {
      return allBuildings;
    }
    
    // Filter buildings based on user's assigned buildings
    if (user.assigned_buildings && user.assigned_buildings.length > 0) {
      return allBuildings.filter(building => 
        user.assigned_buildings.includes(building.id)
      );
    }
    
    // If no specific buildings assigned, return empty array
    return [];
  }, [user, visitorPermissions.canAccessAllBuildings]);

  // Get permission summary for UI display
  const getPermissionSummary = useCallback(() => {
    if (!visitorPermissions) return null;
    
    const summary = {
      role: userRole,
      level: 'none',
      features: [],
      restrictions: []
    };
    
    // Determine permission level
    if (visitorPermissions.canManageVisitors) {
      summary.level = 'full';
      summary.features.push('Full visitor management');
    } else if (visitorPermissions.canApproveVisitors) {
      summary.level = 'approval';
      summary.features.push('Visitor approval');
    } else if (visitorPermissions.canCheckInVisitors) {
      summary.level = 'checkin';
      summary.features.push('Check-in/out visitors');
    } else if (visitorPermissions.canViewVisitors) {
      summary.level = 'view';
      summary.features.push('View visitors');
    }
    
    // Add specific features
    if (visitorPermissions.canViewAnalytics) {
      summary.features.push('Analytics');
    }
    if (visitorPermissions.canExportData) {
      summary.features.push('Data export');
    }
    if (roleFeatures.bulkOperations) {
      summary.features.push('Bulk operations');
    }
    
    // Add restrictions
    if (!visitorPermissions.canAccessAllBuildings) {
      summary.restrictions.push('Limited to assigned buildings');
    }
    if (roleFeatures.dashboardAccess === 'basic') {
      summary.restrictions.push('Basic dashboard only');
    }
    if (roleFeatures.analyticsAccess === 'none') {
      summary.restrictions.push('No analytics access');
    }
    
    return summary;
  }, [visitorPermissions, userRole, roleFeatures]);

  // Check if feature is available for current user
  const isFeatureAvailable = useCallback((feature) => {
    switch (feature) {
      case 'dashboard':
        return roleFeatures.dashboardAccess !== 'none';
      case 'analytics':
        return roleFeatures.analyticsAccess !== 'none';
      case 'reporting':
        return roleFeatures.reportingAccess !== 'none';
      case 'settings':
        return roleFeatures.settingsAccess !== 'none';
      case 'bulk_operations':
        return roleFeatures.bulkOperations;
      case 'advanced_filters':
        return roleFeatures.advancedFilters;
      case 'system_config':
        return roleFeatures.systemConfiguration;
      default:
        return false;
    }
  }, [roleFeatures]);

  return {
    loading,
    error,
    user,
    userRole,
    permissions: visitorPermissions,
    roleFeatures,
    hasPermission,
    canPerformAction,
    getAccessibleBuildings,
    getPermissionSummary,
    isFeatureAvailable
  };
};

export default useVisitorPermissions;